import React, { useState } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  CheckForm,
  Flex,
  Form,
  Group,
  Modal,
  TextArea,
} from "@samskip/frontend-components";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { QuoteDeclineIds, LabelConstants } from "@assets/constants";
import { useAppSelector } from "@assets/hooks/redux";
import { QuoteDecline, CommonTypes } from "@assets/types";
import { ArrayUtilities } from "@assets/utilities";
import { getErrorMessage } from "@assets/utilities/translateUtilities";
import { quoteReasonsState } from "@state/slices/quoteReasons";

type QuoteDeclineModalProps = {
  declineQuote: CommonTypes.TwoParamCallbackFunction<string, QuoteDecline>;
  quoteReference: string;
};

const QuoteDeclineModal = ({
  declineQuote,
  quoteReference,
}: QuoteDeclineModalProps) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const declineReasonList = useAppSelector(quoteReasonsState).reasons;
  const defaultFormValues: QuoteDecline = {
    DeclineId: declineReasonList[0].DeclineId,
    DeclineMessage: "",
  };

  const declineSchema = yup.object({
    DeclineId: yup
      .number()
      .required(LabelConstants.ERROR_LABELS.REQUIRED_FIELD),
    DeclineMessage: yup.string().when("DeclineId", {
      is: QuoteDeclineIds.DECLINE_OTHER,
      then: (schema) =>
        schema.required(LabelConstants.ERROR_LABELS.REQUIRED_FIELD),
    }),
  });

  const {
    formState: { errors },
    handleSubmit,
    register,
    watch,
    reset,
  } = useForm({
    defaultValues: defaultFormValues,
    resolver: yupResolver(declineSchema),
    mode: "onTouched",
  });

  const openModal = () => {
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
  };

  const submitDeclineReason = async (data: QuoteDecline) => {
    const declineReason = ArrayUtilities.getObject(
      declineReasonList,
      data.DeclineId,
      "DeclineId"
    );
    if (data.DeclineId === QuoteDeclineIds.DECLINE_OTHER) {
      declineQuote(quoteReference, data);
    } else {
      declineQuote(quoteReference, declineReason || data);
    }
    closeModal();
    reset();
  };

  const getLabelForReason = (declineId: QuoteDeclineIds) => {
    switch (declineId) {
      case QuoteDeclineIds.DECLINE_OTHER:
        return t(LabelConstants.QUOTE_REASON_LABELS.Other);
      case QuoteDeclineIds.DECLINE_ANOTHER_COMPANY:
        return t(LabelConstants.QUOTE_REASON_LABELS.AnotherCompany);
      case QuoteDeclineIds.DECLINE_NO_TRANSPORT:
        return t(LabelConstants.QUOTE_REASON_LABELS.NoTransportNeed);
    }
  };

  return (
    <>
      <Button onClick={openModal} variant="secondary">
        {t("LABEL_DECLINE")}
      </Button>
      <Modal
        closeIcon
        closeOnOutsideClick
        onClose={closeModal}
        open={isModalOpen}
        primaryButtonProps={{
          onClick: handleSubmit(submitDeclineReason),
          type: "submit",
          children: t("LABEL_SUBMIT_AND_DECLINE"),
        }}
        secondaryButtonProps={{
          onClick: closeModal,
          children: t("LABEL_CANCEL"),
        }}
        size="small"
        title={t("LABEL_DECLINE_QUOTE")}
      >
        <Flex gap="small" vertical>
          {t("TEXT_DECLINE_QUOTE")}
          <Form>
            <Group vertical>
              {declineReasonList.map((declineReason: QuoteDecline) => (
                <CheckForm
                  checked={declineReason.DeclineId == watch("DeclineId")}
                  id={declineReason.DeclineId.toString()}
                  key={declineReason.DeclineId}
                  label={getLabelForReason(declineReason.DeclineId)}
                  type="radio"
                  value={declineReason.DeclineId}
                  {...register("DeclineId")}
                />
              ))}
            </Group>
            {watch("DeclineId") == QuoteDeclineIds.DECLINE_OTHER && (
              <TextArea
                {...register("DeclineMessage")}
                errorMessage={getErrorMessage("DeclineMessage", errors)}
                hasError={errors.DeclineMessage != null}
                id="decline-message"
                placeholder={t("TEXT_SPECIFY_REASON")}
              />
            )}
          </Form>
        </Flex>
      </Modal>
    </>
  );
};

export default QuoteDeclineModal;
